// Wizard step 4 — pass back (slice 4a-3).
//
// Three sections:
//   System rows — Naya ToS + Privacy Policy. Informational, NOT stored in
//     back_links; appended at render time. The owner can't remove them.
//   Owner terms — an optional free-text block (max OWNER_TERMS_MAX_LENGTH).
//   Links       — up to MAX_BACK_LINKS editable rows. Up/down reorder buttons
//     (drag was deemed not worth the dependency for V1).
//
// All cosmetic — editable post-activation.

import {
  BACK_LINK_TYPES,
  MAX_BACK_LINKS,
  OWNER_TERMS_MAX_LENGTH,
} from '@naya/shared'
import type { BackLink, BackLinkType } from '@naya/shared'

import type { BuilderController } from '../hooks/useBuilder.js'

const LINK_TYPE_LABELS: Record<BackLinkType, string> = {
  url: 'Website',
  phone: 'Phone',
  email: 'Email',
  address: 'Address',
  facebook: 'Facebook',
  instagram: 'Instagram',
  tiktok: 'TikTok',
}

const VALUE_PLACEHOLDER: Record<BackLinkType, string> = {
  url: 'https://…',
  phone: '09XXXXXXXXX',
  email: 'hello@…',
  address: 'Street, city',
  facebook: 'facebook.com/…',
  instagram: 'instagram.com/…',
  tiktok: 'tiktok.com/@…',
}

function LinkRow({
  link,
  index,
  count,
  onChange,
  onRemove,
  onMove,
}: {
  link: BackLink
  index: number
  count: number
  onChange: (next: BackLink) => void
  onRemove: () => void
  onMove: (dir: -1 | 1) => void
}) {
  return (
    <div className="program-link-row">
      <div className="program-link-row-top">
        <select
          className="owner-input program-link-type"
          value={link.type}
          onChange={(e) => onChange({ ...link, type: e.target.value as BackLinkType })}
          aria-label="Link type"
        >
          {BACK_LINK_TYPES.map((t) => (
            <option key={t} value={t}>
              {LINK_TYPE_LABELS[t]}
            </option>
          ))}
        </select>
        <div className="program-link-row-actions">
          <button
            type="button"
            className="program-link-btn"
            disabled={index === 0}
            onClick={() => onMove(-1)}
            aria-label="Move up"
          >
            ↑
          </button>
          <button
            type="button"
            className="program-link-btn"
            disabled={index === count - 1}
            onClick={() => onMove(1)}
            aria-label="Move down"
          >
            ↓
          </button>
          <button
            type="button"
            className="program-link-btn program-danger"
            onClick={onRemove}
            aria-label="Remove link"
          >
            Remove
          </button>
        </div>
      </div>
      <input
        type="text"
        className="owner-input"
        placeholder={VALUE_PLACEHOLDER[link.type]}
        value={link.value}
        maxLength={500}
        onChange={(e) => onChange({ ...link, value: e.target.value })}
        aria-label="Link value"
      />
      <input
        type="text"
        className="owner-input"
        placeholder="Display text, e.g. Visit our website"
        value={link.display_text}
        maxLength={60}
        onChange={(e) => onChange({ ...link, display_text: e.target.value })}
        aria-label="Link display text"
      />
    </div>
  )
}

export function StepBack({ builder }: { builder: BuilderController }) {
  const { draft } = builder
  const links = draft.back_links

  function setLinks(next: BackLink[]) {
    builder.update({ back_links: next })
  }

  function addLink() {
    if (links.length >= MAX_BACK_LINKS) return
    setLinks([...links, { type: 'url', value: '', display_text: '' }])
  }

  function updateLink(index: number, next: BackLink) {
    setLinks(links.map((l, i) => (i === index ? next : l)))
  }

  function removeLink(index: number) {
    setLinks(links.filter((_, i) => i !== index))
  }

  function moveLink(index: number, dir: -1 | 1) {
    const target = index + dir
    if (target < 0 || target >= links.length) return
    const next = [...links]
    const a = next[index]
    const b = next[target]
    if (!a || !b) return
    next[index] = b
    next[target] = a
    setLinks(next)
  }

  return (
    <div className="program-step">
      <h2>What goes on the back of the pass?</h2>
      <p className="program-step-sub">
        The back holds terms, contact info, and links. All of this is editable
        later.
      </p>

      <div className="program-field-block">
        <label
          className="program-field-label"
          htmlFor="program-pass-description"
        >
          Pass description (optional)
        </label>
        <span className="program-field-hint">
          A short blurb shown on the back of the pass — how the program works,
          what makes it special, anything you want customers to read.
        </span>
        <textarea
          id="program-pass-description"
          className="owner-input"
          value={draft.pass_design.pass_description}
          maxLength={200}
          rows={3}
          onChange={(e) =>
            builder.update({
              pass_design: {
                ...draft.pass_design,
                pass_description: e.target.value,
              },
            })
          }
        />
      </div>

      <div className="program-field-block">
        <label className="program-field-label" htmlFor="owner-terms">
          Your program terms
        </label>
        <span className="program-field-hint">
          Add anything specific about your program — expiry, restrictions, fine
          print. Optional.
        </span>
        <textarea
          id="owner-terms"
          className="owner-input program-textarea"
          rows={4}
          maxLength={OWNER_TERMS_MAX_LENGTH}
          value={draft.owner_terms}
          onChange={(e) => builder.update({ owner_terms: e.target.value })}
        />
        <span className="program-char-count">
          {draft.owner_terms.length}/{OWNER_TERMS_MAX_LENGTH}
        </span>
      </div>

      <div className="program-field-block">
        <span className="program-field-label">Links</span>
        <span className="program-field-hint">
          Up to {MAX_BACK_LINKS}. Customers tap these from the back of the pass.
        </span>
        <div className="program-link-list">
          {links.map((link, i) => (
            <LinkRow
              key={i}
              link={link}
              index={i}
              count={links.length}
              onChange={(next) => updateLink(i, next)}
              onRemove={() => removeLink(i)}
              onMove={(dir) => moveLink(i, dir)}
            />
          ))}
        </div>
        <button
          type="button"
          className="owner-btn program-add-btn"
          disabled={links.length >= MAX_BACK_LINKS}
          onClick={addLink}
        >
          + Add link
        </button>
      </div>
    </div>
  )
}
